import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateManagementCompany } from "../services/managementUpdateService";
import { validateManagementForm } from "../utils/managementModalUtils";
import { selectedSelectedManagement } from "../../../../../redux/mgmtCo/selectedManagementSlice";
import { fetchManagementCompanies } from "../../../../../redux/mgmtCo/managementSlice";
import {
  selectCurrentPage,
  selectRowsPerPage,
} from "../../../../../redux/mgmtCo/paginationSlice";

const shallowEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;
  if (!obj1 || !obj2) return false;
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;
  for (let key of keys1) {
    if (obj1[key] !== obj2[key]) return false;
  }
  return true;
};

const useViewManagementCompany = (initialCompany = null, updateList) => {
  const dispatch = useDispatch();
  const selectedCompanyFromRedux = useSelector(selectedSelectedManagement);
  const company = initialCompany || selectedCompanyFromRedux || {};

  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState(company);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentPage = useSelector(selectCurrentPage);
  const rowsPerPage = useSelector(selectRowsPerPage);

  useEffect(() => {
    if (!shallowEqual(company, formData)) {
      setFormData(company);
    }
  }, [company]);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    setValidationErrors(validateManagementForm(updatedFormData));
  };

  const handleToggleEdit = () => {
    setIsEditMode((prev) => !prev);
    setValidationErrors({});
  };

  const handleSave = async (autoSave = false) => {
    setIsSubmitting(true);
    const errors = validateManagementForm(formData);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setIsSubmitting(false);
      return false;
    }
    try {
      const success = await updateManagementCompany(
        formData.managementId,
        formData
      );
      if (success && typeof updateList === "function") {
        updateList();
      }
      // For autoSave, remain in edit mode; for manual save, exit edit mode.
      if (!autoSave) {
        setIsEditMode(false);
      }
      dispatch(
        fetchManagementCompanies({ currentPage, rowsPerPage, searchQuery: "" })
      );
      return true;
    } catch (error) {
      setValidationErrors({ form: error.message });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearValidation = () => {
    setValidationErrors({});
  };

  return {
    isEditMode,
    setIsEditMode,
    formData,
    handleFieldChange,
    handleSave,
    handleToggleEdit,
    validationErrors,
    isSubmitting,
    clearValidation,
  };
};

export default useViewManagementCompany;
